import { FC } from "react";

import {
  ConfirmationDialog,
  type ConfirmationDialogProps,
  Paragraph,
  useToast,
} from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import { capitalize } from "lodash";

type Props = {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Callback for closing the dialog. */
  onClose: () => void;
  /** Callback for actually deleting the resource once confirmed. */
  onDelete: () => Promise<unknown>;
  /** A short label for the resource being deleted, like "sync" or "audience". */
  resourceLabel: string;
  /** Optional alternate title for the confirmation dialog. */
  title?: string;
  /** Optional alternate content for the confirmation dialog.*/
  content?: React.ReactNode;
  /** Show a toast after the deletion is done. */
  showToast?: boolean;
  /** Dialog width. */
  width?: ConfirmationDialogProps["width"];
  /** Optional alternate text for the confirm button. */
  confirmButtonText?: string;
  /** Disable the confirm button. */
  isConfirmButtonDisabled?: boolean;
};

export const DeleteConfirmationModal: FC<Readonly<Props>> = ({
  isOpen,
  onClose,
  resourceLabel,
  title,
  onDelete,
  content,
  showToast = true,
  width,
  confirmButtonText = "Delete",
  isConfirmButtonDisabled,
}) => {
  const { toast } = useToast();

  // TODO: update to allow better toast messages, or remove and force
  // parent `onDelete` to handle toasts.
  const handleDelete = async () => {
    try {
      await onDelete();
      if (showToast) {
        toast({
          id: "single-delete-toast",
          title: `${capitalize(resourceLabel)} was deleted`,
          variant: "success",
        });
      }
    } catch (error) {
      captureException(error);
      if (showToast) {
        toast({
          id: "single-delete-toast",
          title: `Failed to delete ${resourceLabel}`,
          variant: "error",
        });
      }
    }
  };

  return (
    <ConfirmationDialog
      confirmButtonText={confirmButtonText}
      isConfirmButtonDisabled={isConfirmButtonDisabled}
      isOpen={isOpen}
      title={title ?? `Delete ${resourceLabel}`}
      variant="danger"
      width={width}
      onClose={onClose}
      onConfirm={handleDelete}
    >
      {content ? (
        content
      ) : (
        <Paragraph>
          Are you sure you want to delete this {resourceLabel}? You won't be
          able to undo this.
        </Paragraph>
      )}
    </ConfirmationDialog>
  );
};
